/**
 * The address printed on an accepted quotation, and what it should open.
 *
 * The verification page is the same Static Web App under a second hostname, not a second
 * deployment - so every path this bundle serves is reachable under both names. That is a
 * problem for exactly one path: `/`. A customer's auditor types the bare address off a
 * printed document, and without this they land on the application root, which redirects to
 * ARMS sign-in for an account they do not have and never will.
 *
 * Static Web Apps route rules cannot match on host, so the decision has to be made here.
 *
 * Everything outside `/verify` goes to the same place rather than only the root, because
 * nothing else on this host can work. `redirectUri` is built from the current origin, and
 * ARMS honours only the redirect URIs registered against the client - which are the
 * application host's. A sign-in started here is refused before a password is typed, and a
 * screen that can never succeed is worse than the one page this host exists to serve.
 *
 * Matched on the hostname's first label rather than a build-time variable, so the web
 * bundle stays identical between the two names it answers to and the pipeline needs no
 * fourth URL to keep in step. The name is public - it is printed on the document.
 */
const LABEL = 'quotation-verify'

/** Whether this host is the one printed on accepted quotations. */
export function isVerificationHost(host: string): boolean {
  // Port stripped because a dev server carries one; case folded because a hostname is
  // case-insensitive and a pasted address is whatever the sender's mail client made of it.
  const name = host.trim().toLowerCase().split(':')[0] ?? ''
  const label = name.split('.')[0] ?? ''

  // `quotation-verify` and `quotation-verify-staging`, and nothing that merely starts with
  // those characters: `quotation-verifier.example.com` is somebody else's host.
  return label === LABEL || label.startsWith(`${LABEL}-`)
}

/**
 * Where to send somebody who arrived on the verification host, or null to leave them be.
 *
 * Pure, and returns the path rather than performing the navigation, so the rule can be
 * asserted without a browser.
 */
export function verificationEntryPath(host: string, pathname: string): string | null {
  if (!isVerificationHost(host)) return null
  if (pathname === '/verify' || pathname.startsWith('/verify/')) return null

  return '/verify'
}
